"use client";

import React from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import { Mail, User, MessageSquare, Send, CheckCircle2 } from "lucide-react";

// --- validace ---
const formSchema = z.object({
  name: z.string().min(2, { message: "Jméno musí mít alespoň 2 znaky." }),
  email: z.string().email({ message: "Zadejte platný e-mail." }),
  message: z.string().min(10, { message: "Zpráva musí mít alespoň 10 znaků." }),
});

function InnerForm() {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { name: "", email: "", message: "" },
  });

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [showModal, setShowModal] = React.useState(false);

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);

    try {
       const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });

      if (!res.ok) throw new Error("Odeslání zprávy selhalo.");

      toast.success("Děkuji za Vaši zprávu!");
      setShowModal(true);
      form.reset();
    } catch (error) {
      console.error("❌ Chyba při odesílání:", error);
      toast.error(
        "Při odesílání zprávy došlo k chybě. Zkuste to prosím znovu."
      );
    } finally {
      setTimeout(() => setIsSubmitting(false), 3000);
    }
  };

  return (
    <>
      <Card className="backdrop-blur-sm bg-white/95 shadow-xl border-gray-200/50 hover:shadow-2xl transition-shadow duration-300">
        <CardHeader className="space-y-1">
        </CardHeader>
        <CardContent>
          <Form {...form}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                form.handleSubmit(onSubmit)(e);
              }}
              className="space-y-6"
            >
              {/* Grid layout pro jméno a email na větších obrazovkách */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:items-start">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <User className="h-4 w-4 text-primary" />
                        Jméno a příjmení
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="Jan Novák"
                          {...field}
                          autoComplete="name"
                          className="transition-all duration-200 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        />
                      </FormControl>
                      <FormMessage className="min-h-[20px]" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-primary" />
                        E-mail
                      </FormLabel>
                      <FormControl>
                        <Input
                          placeholder="vas.email@domena.cz"
                          {...field}
                          autoComplete="email"
                          className="transition-all duration-200 focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        />
                      </FormControl>
                      <FormMessage className="min-h-[20px]" />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4 text-primary" />
                      Zpráva
                    </FormLabel>
                    <FormControl>
                      <Textarea
                        placeholder="Vaše zpráva..."
                        rows={10}
                        {...field}
                        className="min-h-[150px] transition-all duration-200 focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                        autoComplete="off"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="flex justify-center pt-2">
                <Button
                  type="submit"
                  size="lg"
                  className={`w-full md:w-auto min-w-[200px] flex items-center justify-center gap-2 transition-all duration-200 hover:scale-105 cursor-pointer ${
                    isSubmitting ? "cursor-wait" : ""
                  }`}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                      Odesílání...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Odeslat zprávu
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex justify-center mb-4">
              <div className="rounded-full bg-green-100 p-3">
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              </div>
            </div>
            <DialogTitle className="text-center text-2xl">
              Děkuji za zprávu!
            </DialogTitle>
            <DialogDescription className="text-center text-base pt-2">
              Vaše zpráva byla úspěšně odeslána. Ozvu se Vám co nejdříve.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center pt-4">
            <Button
              onClick={() => {
                setShowModal(false);
                setIsSubmitting(false);
              }}
              className="min-w-[120px]"
            >
              Zavřít
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ContactForm() {
  return (
    <section id="kontakt" className="relative py-20 px-4 bg-gradient-to-b from-gray-50 to-gray-100">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="font-bold text-4xl md:text-5xl mb-4 bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
            Kontaktujte mě
          </h2>
          <p className="text-gray-600 text-lg max-w-2xl mx-auto">
            Máte dotaz nebo chcete domluvit zakázku? Neváhejte mě kontaktovat
            pomocí formuláře.
          </p>
        </div>
        <InnerForm />
      </div>
    </section>
  );
}

export default ContactForm;
